/**
 * @most/i18n — react-i18next for all three SPAs.
 *
 * RU is the default AND the fallback, and is guaranteed complete (docs/06 §8): `tk → ru`,
 * `en → ru`, missing key → the RU string. The client (Merdan) is Russian-speaking and
 * non-technical; an English string leaking into his UI is a bug.
 *
 * Russian has THREE plural forms (one / few / many). Every countable string uses i18next
 * plurals — `key_one` / `key_few` / `key_many` — never string concatenation.
 *
 * Product and profile CONTENT is not translated (decision D4): names, descriptions and
 * composition render as the producer entered them (Russian) in every UI language. Category
 * names are the one exception and come from the API's `name_ru/name_en/name_tk` fields.
 */
import i18n, { type Resource, type ResourceLanguage } from 'i18next';
import { initReactI18next, useTranslation } from 'react-i18next';

import ruCommon from './locales/ru/common.json';
import ruAuth from './locales/ru/auth.json';
import ruBuyer from './locales/ru/buyer.json';
import ruProducer from './locales/ru/producer.json';
import ruAdmin from './locales/ru/admin.json';

import enCommon from './locales/en/common.json';
import enAuth from './locales/en/auth.json';
import enBuyer from './locales/en/buyer.json';

import tkCommon from './locales/tk/common.json';
import tkBuyer from './locales/tk/buyer.json';

export const LOCALES = ['ru', 'en', 'tk'] as const;
export type Locale = (typeof LOCALES)[number];

export const DEFAULT_LOCALE: Locale = 'ru';

export const LOCALE_LABELS: Record<Locale, string> = {
  ru: 'Русский',
  en: 'English',
  tk: 'Türkmen',
};

export const NAMESPACES = ['common', 'auth', 'buyer', 'producer', 'admin'] as const;

const resources = {
  ru: {
    common: ruCommon,
    auth: ruAuth,
    buyer: ruBuyer,
    producer: ruProducer,
    admin: ruAdmin,
  },
  en: {
    common: enCommon,
    auth: enAuth,
    buyer: enBuyer,
  },
  tk: {
    common: tkCommon,
    buyer: tkBuyer,
  },
} as const;

const STORAGE_KEY = 'most_locale';

export function isLocale(value: unknown): value is Locale {
  return typeof value === 'string' && (LOCALES as readonly string[]).includes(value);
}

/** Guests persist the choice in localStorage; logged-in users also PATCH `/me` with it. */
export function readStoredLocale(): Locale {
  if (typeof window === 'undefined') return DEFAULT_LOCALE;
  const stored = window.localStorage.getItem(STORAGE_KEY);
  return isLocale(stored) ? stored : DEFAULT_LOCALE;
}

export function storeLocale(locale: Locale): void {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(STORAGE_KEY, locale);
}

/** Keeps `<html lang>` in step with the active locale (docs/06 §12). */
export function applyDocumentLocale(locale: Locale): void {
  if (typeof document !== 'undefined') document.documentElement.lang = locale;
}

let initialized = false;

/**
 * Options an app may pass when it bootstraps i18n.
 *
 * `resources` are MERGED OVER the built-in catalogs, per locale and per namespace, rather than
 * replacing them. An app that supplies its own `admin` bundle still keeps `common` and `auth`,
 * which is what its own screens actually reach for — a replace would leave every shared string
 * (buttons, statuses, validation) resolving to a raw key.
 */
export interface InitI18nOptions {
  /** Starting locale. Defaults to the stored one, then to RU. */
  locale?: Locale;
  /** Per-locale, per-namespace bundles, merged over the built-ins. */
  resources?: Partial<Record<Locale, Record<string, unknown>>>;
  /** The namespace an app's bare `t('foo')` resolves against. */
  defaultNS?: string;
}

/**
 * initI18n accepts EITHER a locale string or an options object.
 *
 * The two shapes exist because the three SPAs were written against `createI18n({resources, …})`
 * while this package exported `initI18n(locale)`, and the alias below tied them together
 * without reconciling the signatures — so the admin app's config object landed in i18next's
 * `lng`, where `getBestMatchFromCodes` did `codes.forEach(...)` on it and threw
 * `TypeError: codes.forEach is not a function`. i18next throws that during `init()`, before
 * React mounts, so the app died with an empty `#root` and a white screen and no visible error.
 *
 * Accepting both shapes is the fix that does not require editing every call site.
 */
export function initI18n(arg?: Locale | InitI18nOptions) {
  const opts: InitI18nOptions = typeof arg === 'string' ? { locale: arg } : (arg ?? {});

  if (initialized) {
    // Vite HMR re-executes an app's `i18n/index.ts` whenever one of its locale files changes,
    // and this guard used to return the live instance untouched — so the new strings never
    // reached i18next and the screen showed raw keys ("categories.stats.rootsHint") until a
    // hard reload. Re-registering the bundles (deep, overwrite) keeps a running dev session
    // in step with the file on disk; in production this branch never runs.
    for (const lng of LOCALES) {
      for (const [ns, bundle] of Object.entries(opts.resources?.[lng] ?? {})) {
        i18n.addResourceBundle(lng, ns, bundle, true, true);
      }
    }
    return i18n;
  }
  initialized = true;

  const locale: Locale = opts.locale ?? readStoredLocale();

  // Merge app-supplied bundles OVER the built-ins, namespace by namespace.
  const merged: Resource = {};
  for (const lng of LOCALES) {
    merged[lng] = {
      ...(resources as unknown as Resource)[lng],
      ...(opts.resources?.[lng] ?? {}),
    } as ResourceLanguage;
  }

  void i18n.use(initReactI18next).init({
    resources: merged,
    lng: locale,
    // RU is the guaranteed-complete catalog. Everything falls back to it, never to a key.
    fallbackLng: DEFAULT_LOCALE,
    ns: NAMESPACES as unknown as string[],
    defaultNS: opts.defaultNS ?? 'common',
    supportedLngs: LOCALES as unknown as string[],
    interpolation: { escapeValue: false },
    returnNull: false,
  });

  applyDocumentLocale(locale);
  return i18n;
}

export async function changeLocale(locale: Locale): Promise<void> {
  storeLocale(locale);
  applyDocumentLocale(locale);
  await i18n.changeLanguage(locale);
}

/** Category names are served per-locale by the API — not from these bundles (docs/06 §8). */
export function categoryName(
  category: { name_ru: string; name_en: string; name_tk: string },
  locale: Locale,
): string {
  switch (locale) {
    case 'en':
      return category.name_en || category.name_ru;
    case 'tk':
      return category.name_tk || category.name_ru;
    default:
      return category.name_ru;
  }
}

/** Absolute dates in tables (docs/06 §8) — no "3 days ago" ambiguity in a B2B ledger. */
export function formatDate(iso: string, locale: Locale): string {
  return new Intl.DateTimeFormat(locale === 'tk' ? 'tk-TM' : locale, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date(iso));
}

export function formatDateTime(iso: string, locale: Locale): string {
  return new Intl.DateTimeFormat(locale === 'tk' ? 'tk-TM' : locale, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(iso));
}

export { i18n };
export { useTranslation, Trans, I18nextProvider } from 'react-i18next';

// ─────────────────────────────────────────────────────────────────────────────
//  Compatibility aliases
// ─────────────────────────────────────────────────────────────────────────────
//
// The three SPAs were written in parallel with this package, against the names they expected
// from a conventional i18n setup. Rather than edit ~30 call sites across three apps — or, far
// worse, let each app keep its own private i18n bootstrap, which is exactly the drift this
// package exists to prevent — the names are aliased here, once.

/** Alias of `LOCALES`. */
export const SUPPORTED_LOCALES = LOCALES;

/** Alias of `Locale`. */
export type SupportedLocale = Locale;

/**
 * Alias of `initI18n`, which is what the three SPAs actually call — with an options object.
 * See initI18n: it accepts both that and a bare locale string, and the object form is why.
 */
export const createI18n = initI18n;

/** The i18next provider, under the name the apps compose at their root. */
export { I18nextProvider as I18nProvider } from 'react-i18next';

/**
 * useLocale — the active locale plus a setter.
 *
 * Changing the locale does two things, and both matter: it swaps the UI chrome, AND (for a
 * logged-in user) persists to `users.locale` — which is the language the backend renders their
 * emails and Telegram messages in. A producer who switches to Turkmen expects the order alert
 * in Turkmen too.
 */
export function useLocale(): {
  locale: Locale;
  setLocale: (locale: Locale) => Promise<void>;
} {
  const { i18n: instance } = useTranslation();
  const current = isLocale(instance.language) ? instance.language : DEFAULT_LOCALE;

  return {
    locale: current,
    setLocale: changeLocale,
  };
}
